/**
 * Normalisers for URL search parameters, shared by the route validators that
 * read the same filters (the dossier overview's and the reporting page's).
 * They live here for the same reason `threeStateBoolean.ts` does: a route
 * module must never become a utility module (see `dossiers/dossiersSearch.ts`'s
 * docblock), and a validator duplicated across routes drifts.
 *
 * They all take `unknown` on purpose. A search parameter arrives from whatever the
 * URL happens to contain, so "malformed" is a normal input rather than a bug,
 * and every shape must resolve to the same "no filter" answer.
 */

/**
 * Normalises a search parameter holding at most one string: anything other
 * than a non-empty string (absent, malformed, the cleared control) is "no
 * filter".
 */
export function toNonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/**
 * Normalises a search parameter holding zero or more strings.
 *
 * TanStack Router's default search codec JSON-encodes/decodes array values
 * symmetrically on both the read (fresh URL load) and write (`navigate()`)
 * side. It also accepts a repeated key (`?brand=a&brand=b`) as an array, but
 * collapses a *single* occurrence (`?brand=a`, for example a hand-written or
 * bookmarked URL) down to a plain string rather than a one-element array — so
 * that form must be normalised too, alongside defending against a missing or
 * malformed value.
 *
 * A hand-edited or bookmarked URL can also repeat the same value
 * (`?brand=a&brand=a`). Deduplicating keeps the search state canonical rather
 * than forwarding redundant `brand[]` values to the backend and creating
 * needless React Query cache-key permutations for what is semantically the
 * same filter. Empty strings are dropped: they are the cleared control, not a
 * value to filter on.
 */
export function toStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return [
      ...new Set(
        value.filter(
          (item): item is string => typeof item === "string" && item !== "",
        ),
      ),
    ];
  }

  return typeof value === "string" && value !== "" ? [value] : [];
}

/**
 * Normalises a search parameter holding a three-state boolean filter: only an
 * actual boolean is a choice, anything else (absent, malformed) is "no
 * filter". An absent value must stay `undefined` rather than collapse to
 * `false`, because `false` is a deliberate choice the request has to send.
 */
export function toThreeStateBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}
